import React, { useState, useEffect, useMemo } from 'react';
import styles from './styles/Step1.module.css';
import inputStyles from './styles/OnboardingInput.module.css';
import sharedStyles from './styles/BrandSignup.module.css';
import { usei18n } from "@/components/context/i18nContext";
import { Luna, ErrorLuna } from './Luna';


const StepEmail = ({ brandSignup, onComplete, loading, error, paths, csrfToken, onBack, canGoBack, showLuna = false }) => {
  const { t } = usei18n();
  const step1Translations = t.brandSignup.step1;

  const [formData, setFormData] = useState({
    email: brandSignup.email || '',
    password: '',
    google_auth: brandSignup.google_auth || false
  });

  const [showPassword, setShowPassword] = useState(false);


  const [formErrors, setFormErrors] = useState({});
  const [enableSubmit, setEnableSubmit] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [errorEverShown, setErrorEverShown] = useState(false);
  const [validationMessages, setValidationMessages] = useState({});

  const hasErrors = () => Object.keys(formErrors).length > 0 || serverError !== null;

  useEffect(() => {
    // Handle server errors from the parent component
    if (error) {
      // Check if it's an email duplicate error
      if (error.toLowerCase().includes('already registered') || error.toLowerCase().includes('already exists')) {
        setServerError('email_taken');
        setFormErrors(prev => ({ ...prev, email: true }));
        setErrorEverShown(true);
      } else {
        setServerError('general');
        setErrorEverShown(true);
      }
    } else {
      setServerError(null);
    }
  }, [error]);

  useEffect(() => {
    // Track if form errors have been shown
    if (hasErrors()) {
      setErrorEverShown(true);
    }
  }, [formErrors]);

  useEffect(() => {
    // Pre-fill if we have initial data from homepage
    const initialData = brandSignup.step_data?.initial;
    if (initialData && !formData.email) {
      setFormData(prev => ({
        ...prev,
        email: initialData.email || ''
      }));
    }
  }, [brandSignup.step_data]);

  useEffect(() => {
    // Email regex from devise config
    const emailRegex = /^[^\s@]+@[^\s@]+$/;

    const isComplete =
      formData.email.trim().length > 0 &&
      emailRegex.test(formData.email) &&
      (formData.google_auth || formData.password.length >= 6);

    if (isComplete && !hasErrors()) {
      setEnableSubmit(true);
    } else {
      setEnableSubmit(false);
    }
  }, [formData, formErrors, serverError]);

  const validateForm = () => {
    const errors = {};
    const messages = {};
    // Email regex from devise config
    const emailRegex = /^[^\s@]+@[^\s@]+$/;

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      messages.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Invalid email format';
      messages.email = 'Please enter a valid email address in the format: user@example.com';
    }

    if (!formData.google_auth && !formData.password.trim()) {
      errors.password = 'Password is required';
      messages.password = 'Password is required';
    } else if (!formData.google_auth && formData.password.length < 6) {
      errors.password = 'Password too short';
      messages.password = 'Password must be at least 6 characters long';
    } else if (!formData.google_auth && formData.password.length > 128) {
      errors.password = 'Password too long';
      messages.password = 'Password must be no more than 128 characters long';
    }

    setFormErrors(errors);
    setValidationMessages(messages);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onComplete(formData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Clear validation message when user starts typing
    if (validationMessages[field]) {
      setValidationMessages(prev => {
        const newMessages = { ...prev };
        delete newMessages[field];
        return newMessages;
      });
    }

    // Clear server error when user modifies the email field
    if (field === 'email' && serverError) {
      setServerError(null);
    }
  };

  const handleBlur = (field) => {
    // Only validate if the field has been touched and has a value
    if (!formData[field] || formData[field].trim() === '') {
      return;
    }

    const errors = {};
    const messages = {};
    const emailRegex = /^[^\s@]+@[^\s@]+$/;

    if (field === 'email') {
      if (!emailRegex.test(formData.email)) {
        errors.email = 'Invalid email format';
        messages.email = 'Please enter a valid email address in the format: user@example.com';
      }
    } else if (field === 'password' && !formData.google_auth) {
      if (formData.password.length < 6) {
        errors.password = 'Password too short';
        messages.password = 'Password must be at least 6 characters long';
      } else if (formData.password.length > 128) {
        errors.password = 'Password too long';
        messages.password = 'Password must be no more than 128 characters long';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(prev => ({ ...prev, ...errors }));
      setValidationMessages(prev => ({ ...prev, ...messages }));
    }
  };

  const handleGoogleSignup = () => {
    // Simple direct link approach like the existing implementations
    window.location.href = paths?.googleAuth || `/brand_signup/${brandSignup.id}/google_auth`;
  };

  return (
    <div className={styles.stepContainer}>

      <div className={styles.title}>{step1Translations.title1}</div>
      {step1Translations.title2 && <div className={styles.title}>{step1Translations.title2}</div>}
      {showLuna && !errorEverShown && <Luna lunaDialogs={step1Translations.Luna} />}
      {showLuna && errorEverShown && (() => {
        let errorDialogs;
        if (serverError === 'email_taken') {
          errorDialogs = step1Translations.LunaEmailTakenError;
        } else {
          errorDialogs = step1Translations.LunaEmailError;
        }
        return <ErrorLuna lunaDialogs={errorDialogs} />;
      })()}

      <div className={styles.formBox}>
        <form className={styles.form} onSubmit={handleSubmit}>

        <div>
          <label className={`${styles.label} ${validationMessages.email ? styles.error : ''}`}>
            {validationMessages.email || step1Translations.emailLabel}
          </label>
          <input
            className={`${inputStyles.input} ${formErrors.email ? `${inputStyles.error}` : ''}`}
            type="email"
            placeholder={step1Translations.emailPlaceholder}
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            disabled={loading}
          />
        </div>

        {!formData.google_auth && (
          <div>
            <label className={`${styles.label} ${validationMessages.password ? styles.error : ''}`}>
              {validationMessages.password || step1Translations.passwordLabel}
            </label>
            <div className={styles.passwordInputContainer}>
              <input
                className={`${inputStyles.input} ${formErrors.password ? `${inputStyles.error}` : ''}`}
                type={showPassword ? "text" : "password"}
                placeholder={step1Translations.passwordPlaceholder}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                onBlur={() => handleBlur('password')}
                disabled={loading}
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                <i className={`ph ${showPassword ? 'ph-eye-slash' : 'ph-eye'}`}></i>
              </button>
            </div>
          </div>
        )}

        <button
          className={styles.button}
          onClick={handleSubmit}
          disabled={loading || !enableSubmit}
        >
          {step1Translations.joinButtonLabel}
        </button>

        <div className={styles.termsNotice}>
          {step1Translations.termsNoticePrefix} <a href="/terms" target="_terms">{step1Translations.termsAndConditions}</a> and <a href="/privacy" target="_terms">{step1Translations.privacyPolicy}</a>.
        </div>

      </form>

        <div className={styles.dividerContainer}>
          <div className={styles.divider} />
          <p>{step1Translations.or}</p>
          <div className={styles.divider} />
        </div>

        <button
          className={styles.googleButton}
          onClick={handleGoogleSignup}
          disabled={loading}
        >
          <img src={'https://assets.nearshored.ai/google.svg'} alt="Google sign in" />
          <p>{step1Translations.googleButtonLabel}</p>
        </button>

        <div className={styles.benefitsList}>
          {step1Translations.Luna.slice(1).map((benefit, index) => (
            <div key={index} className={styles.benefitItem}>
              {benefit}
            </div>
          ))}
        </div>

        <div className={styles.factorySignupLink}>
          Are you a factory? <a href="/sign-up">Log in or sign up here</a>
        </div>
      </div>

      {canGoBack && (
        <a
          className={sharedStyles.backLink}
          onClick={(e) => {
            e.preventDefault();
            onBack();
          }}
        >
          ← Back
        </a>
      )}
    </div>


  );
};

export default StepEmail;
